import { useCallback, useState } from "react"
import { useField, useFormikContext } from "formik"

import { FinalSuccessDialogResources } from "../finalSuccessDialog/FinalSuccessDialogResources"
import { computeOrderFormStatus, OrderFormStatus } from "../../mainForm/helpers"
import { MeetingServiceOrder } from "../../../../../modules/merchant/accounting/accounting/accountingTypes"
import { useDateTime } from "../../../../../modules/localization/useDateTime"
import { CreateUpdateBooking } from "../../createOrderTypes"
import { useCreateOrderFormConfig, useCreateOrderFormSetOrder } from "../../mainForm/CreateOrderFormContext"
import { DeliveryTimesField, DynamicConfigKey, DynamicMainFormValues, MainOrderFormikRef } from "../form/formTypes"
import { CreateUpdateBookingResponse } from "../../createOrderApi"
import { useResourceBookingDraft } from "./ResourceBookingDraftContext"
import { useConfirmBookingDialogActions } from "./ResourceBookingConfirmDialogContext"
import { ResourceBooking } from "./ResourceBooking"
import { getMeetingLocationFromBookedResources } from "./helpers"
import { CreateOrderType } from "../../helpers"

import { getResponsibleSeed } from "../../../../../modules/order/pages/helpers"
import { defaultInitialEventState, defaultMultipleDeliveries, defaultOrderContextState } from "../../defaults"
import { useRefreshCalendar } from "./context/RefreshCalendarProvider"

type ResourceBookingWrapperProps = Readonly<{
    handleChangeOrderType: (type: CreateOrderType) => void
    orderFormStatusRef: React.MutableRefObject<OrderFormStatus>
    orderDetails?: MeetingServiceOrder
    outerFormik: MainOrderFormikRef
}>

export function ResourceBookingWrapper({
    handleChangeOrderType,
    orderFormStatusRef,
    orderDetails,
    outerFormik,
}: ResourceBookingWrapperProps) {
    const { handleClose, handleOutlookBookingCompleted, availableModules } = useCreateOrderFormConfig()
    const { setOrderContext } = useCreateOrderFormSetOrder()
    const { setConfirmBookingDialog } = useConfirmBookingDialogActions()
    const [finalSuccessDialog, setFinalSuccessDialog] = useState<{
        response: CreateUpdateBookingResponse
        isNewOrder: boolean
    } | null>(null)

    const dateTimeFactory = useDateTime()

    const {
        draftEvent,
        fullResources: fullResourcesList,
        setDraftEvent,
        handleChangeInitEventState,
        initialEventStateId,
    } = useResourceBookingDraft()
    const [{ value: multipleDeliveries }, , multipleDeliveriesHelpers] = useField<DeliveryTimesField>(
        DynamicConfigKey.MULTIPLE_DELIVERIES,
    )

    const { setValues } = useFormikContext<DynamicMainFormValues>()
    const { triggerRefetch } = useRefreshCalendar()

    const setMany = useCallback(
        (patch: Partial<DynamicMainFormValues>) => {
            setValues((prev) => ({ ...prev, ...patch }))
        },
        [setValues],
    )

    const handleOpenSuccessDialog = (
        orderDetails: CreateUpdateBookingResponse,
        orderContent: CreateUpdateBooking,
        isNewOrder: boolean,
    ) => {
        const { order } = orderDetails
        const { id, meetingDetails, bookingDetails, messageToServiceCenter } = order

        const resourcesList = bookingDetails?.resources ?? []

        const person = getResponsibleSeed(order.organizers)

        // Reconcile the draft with what was actually saved: the dialog can drop resources or move the
        // time before submitting, and the order query is not refetched while the dialog stays open.
        // The response carries resource ids but not their names or types, so the existing list stays
        // the source for those. An empty response means "nothing to reconcile", never "no booking".
        const savedResourceIds = resourcesList.map((resource) => String(resource.id))
        const savedFullResources = savedResourceIds.length
            ? (fullResourcesList ?? []).filter((resource) => savedResourceIds.includes(String(resource.id)))
            : (fullResourcesList ?? [])
        const savedStart = meetingDetails.startDateTime
            ? dateTimeFactory.toApiISOMinutesFormat(dateTimeFactory.fromApi(meetingDetails.startDateTime))
            : ""
        const savedEnd = meetingDetails.endDateTime
            ? dateTimeFactory.toApiISOMinutesFormat(dateTimeFactory.fromApi(meetingDetails.endDateTime))
            : ""
        const savedEvent = draftEvent
            ? {
                  ...draftEvent,
                  ...(savedResourceIds.length ? { resourceIds: savedResourceIds } : {}),
                  ...(savedStart && savedEnd ? { start: savedStart, end: savedEnd } : {}),
              }
            : null

        setFinalSuccessDialog({ response: orderDetails, isNewOrder })
        setDraftEvent(savedEvent, savedFullResources)
        handleChangeInitEventState({
            // A booking saved in this session becomes the new "Undo all" baseline. Without it the
            // baseline stays null while `initValues.id` still shows the button, so undo wipes.
            event: savedEvent,
            initValues: {
                id,
                meetingName: meetingDetails.meetingName || "",
                organizers: person!,
                attendees: meetingDetails.attendees || [],
                resources: resourcesList,
                messageToServiceCenter: messageToServiceCenter || "",
                isPrivate: Boolean(meetingDetails.isPrivate),
                isExternal: Boolean(meetingDetails.isExternal),
            },
            isUpdated: false,
            fullResources: savedFullResources,
        })

        const fullResourcesById = new Map((fullResourcesList ?? []).map((r) => [r.id, r]))
        const firstBookedRoomId = resourcesList.find((booked) => fullResourcesById.get(booked.id)?.type?.id === 1)?.id

        // The booking goes through this single patch rather than a separate `setFieldValue`: Formik
        // resolves `setValues(fn)` against the render-time state, so an earlier field write in the
        // same tick would be overwritten by it.
        const patch: Partial<DynamicMainFormValues> = {
            event: savedEvent,
            fullResourcesList: savedFullResources,
            resources: resourcesList,
            [DynamicConfigKey.MEETING_NAME]: meetingDetails.meetingName || "",
            [DynamicConfigKey.REGISTER_PARTICIPANTS]: meetingDetails.attendees || [],
            [DynamicConfigKey.PRIVATE_MEETING]: Boolean(meetingDetails.isPrivate),
            [DynamicConfigKey.EXTERNAL_MEETING]: Boolean(meetingDetails.isExternal),
            [DynamicConfigKey.DELIVERY_LOCATIONS]: firstBookedRoomId ? String(firstBookedRoomId) : "",
            // Reopening the dialog reads this back out of the outer form, so the save path has to
            // write it too — closing without saving already does.
            [DynamicConfigKey.SERVICE_CENTER_MESSAGE]: messageToServiceCenter || "",
        }

        if (person) {
            patch[DynamicConfigKey.MEETING_RESPONSIBLE] = person
        }

        setMany(patch)

        if (!multipleDeliveries?.deliveryDate) {
            const deliveryDate = orderContent.meetingDetails.startDateTime
            multipleDeliveriesHelpers.setValue({
                ...defaultMultipleDeliveries,
                deliveryDate: deliveryDate.split(" ")[0],
            })
        }

        if (handleOutlookBookingCompleted) {
            const meetingLocation = getMeetingLocationFromBookedResources({
                bookedResources: resourcesList,
                fullResourcesList,
            })

            handleOutlookBookingCompleted({
                orderId: id,
                startDateTime: meetingDetails.startDateTime,
                endDateTime: meetingDetails.endDateTime,
                meetingLocation,
            })
        }
    }

    const handleClearResourceIds = () => {
        setDraftEvent(null)
        setConfirmBookingDialog(false)

        if (initialEventStateId) {
            handleChangeInitEventState({ isUpdated: true })
        }
    }

    const handleCloseFinalSuccessDialog = (shouldRefresh = true) => {
        setFinalSuccessDialog(null)
        shouldRefresh && triggerRefetch()
    }
    const resetBookingToInitialState = () => {
        orderFormStatusRef.current = computeOrderFormStatus()
        setOrderContext(defaultOrderContextState)
        outerFormik.current?.resetForm()
        handleChangeInitEventState(defaultInitialEventState)
        setDraftEvent(null)
    }

    return (
        <>
            <ResourceBooking
                handleOpenSuccessDialog={handleOpenSuccessDialog}
                handleClearResourceIds={handleClearResourceIds}
                orderDetails={orderDetails}
                orderFormStatusRef={orderFormStatusRef}
            />
            {finalSuccessDialog && (
                <FinalSuccessDialogResources
                    orderDetails={finalSuccessDialog.response}
                    isNewOrder={finalSuccessDialog.isNewOrder}
                    onClose={handleCloseFinalSuccessDialog}
                    handleCloseMainDialog={handleClose}
                    handleOpenMeetingService={handleChangeOrderType}
                    orderFormStatusRef={orderFormStatusRef}
                    resetBookingToInitialState={resetBookingToInitialState}
                    availableModules={availableModules}
                />
            )}
        </>
    )
}
